import { trigger } from '@angular/animations';

import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { fadeIfSlow } from '../../../animations/fade.if-slow.animation';
import { elementIsInputLike } from '../../../lib/element-is-input-like';
import { Directory } from '../../../lib/get-files-from-event';
import { FromUrlFile } from '../../../services/upload-from-url.service';
import { DragDropDirective } from '../../directives/drag-and-drop.directive';
import { StickyComponent } from '../sticky/sticky.component';

@Component({
  selector: 'app-drop',
  templateUrl: './drop.component.html',
  styleUrls: ['./drop.component.scss'],
  animations: [trigger('dragHover', fadeIfSlow)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DragDropDirective, StickyComponent],
})
export class DropComponent {
  @Output() onFileDrop = new EventEmitter<File[]>();
  @Output() onFileUrlDrop = new EventEmitter<FromUrlFile[]>();
  @Output() onFileDropStructured = new EventEmitter<(File | Directory)[]>();
  @Input() borderStyle: 'normal' | 'full' | 'round' | 'rounded' = 'normal';
  @Input() hoverStyle: 'none' | 'white' | 'replace' = 'none';
  @Input() noTopMargin: boolean;
  @Input() darkBackground: boolean;
  @Input() disable: boolean;
  @Input() default: boolean;
  dragHover = false;

  @HostListener('window:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    if (!this.default) return;
    if (elementIsInputLike(document.activeElement) || elementIsInputLike(<Element>event.target)) return;
    event.preventDefault();
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type === 'text/plain') {
        item.getAsString(url => {
          if (!url.startsWith('http')) return;
          const name = url.split('/').pop().split('?')[0];
          const extension = name.split('.').pop();
          if (!extension) return;
          const file = <FromUrlFile>{
            name,
            url,
            size: 100_000,
            source: 'url',
          };
          this.onFileUrlDrop.emit([file]);
        });
      }

      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        this.fileDrop([file]);
      }
    }
  }

  fileDrop(items: (File | Directory)[]) {
    this.onFileDrop.emit(this.getFilesFlat(items));
    this.onFileDropStructured.emit(items);
  }

  /**
   * @returns either the top level files, or if the top level is one directory, returns the files inside that directory.
   */
  private getFilesFlat(items: (File | Directory)[]) {
    if (!items.length) return [] as File[];
    const files = items.filter(f => f instanceof File) as File[];
    if (files.length) return files;

    const directories = items.filter(f => !(f instanceof File)) as Directory[];
    if (directories.length !== 1) return [] as File[];

    return (directories[0]?.items?.filter(f => f instanceof File) as File[]) || ([] as File[]);
  }
}
